import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, BookOpen, Star, Eye, X } from 'lucide-react';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ApiService from '../services/api';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ─── Vicharniye Mahurat Modal ─────────────────────────────────────────────────

const VicharniyeMahuratModal = ({ isOpen, onClose, onSave, item, isEditing }) => {
  const [formData, setFormData] = useState({ title: '', description: '', sloka: '', profitAndLoss: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (isEditing && item) {
        setFormData({
          title: item.title || '',
          description: item.description || '',
          sloka: item.sloka || '',
          profitAndLoss: item.profitAndLoss || '',
          isActive: item.isActive !== undefined ? item.isActive : true,
        });
      } else {
        setFormData({ title: '', description: '', sloka: '', profitAndLoss: '', isActive: true });
      }
      setErrors({});
    }
  }, [isOpen, isEditing, item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      let response;
      if (isEditing) {
        response = await ApiService.updateVicharniyeMahurat(item.id, formData);
      } else {
        response = await ApiService.createVicharniyeMahurat(formData);
      }
      if (response.success) {
        toast.success(isEditing ? 'Vicharniye Mahurat updated!' : 'Vicharniye Mahurat created!');
        onSave();
      } else {
        setErrors({ submit: response.message || 'An error occurred' });
      }
    } catch (error) {
      setErrors({ submit: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Vicharniye Mahurat' : 'Add Vicharniye Mahurat'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title of Muhurt *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter title of muhurt"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <div className="bg-white">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                className=" mb-12"
                placeholder="Enter description (no word limit)"
              />
            </div>
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sloka</label>
            <textarea
              name="sloka"
              value={formData.sloka}
              onChange={handleChange}
              rows={6}
              className="w-full px-3 min-h-48 h-auto py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Enter sloka"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Profit and Loss</label>
            <input
              type="text"
              name="profitAndLoss"
              value={formData.profitAndLoss}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter profit and loss details"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
              ) : (
                <>{isEditing ? 'Update' : 'Create'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Vedon ke Vichar Modal ────────────────────────────────────────────────────

const VedonKeVicharModal = ({ isOpen, onClose, onSave, item, isEditing }) => {
  const [formData, setFormData] = useState({ title: '', description: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (isEditing && item) {
        setFormData({
          title: item.title || '',
          description: item.description || '',
          isActive: item.isActive !== undefined ? item.isActive : true,
        });
      } else {
        setFormData({ title: '', description: '', isActive: true });
      }
      setErrors({});
    }
  }, [isOpen, isEditing, item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title of vichar is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      let response;
      if (isEditing) {
        response = await ApiService.updateVedonKeVichar(item.id, formData);
      } else {
        response = await ApiService.createVedonKeVichar(formData);
      }
      if (response.success) {
        toast.success(isEditing ? 'Vedon ke Vichar updated!' : 'Vedon ke Vichar created!');
        onSave();
      } else {
        setErrors({ submit: response.message || 'An error occurred' });
      }
    } catch (error) {
      setErrors({ submit: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Vedon ke Vichar' : 'Add Vedon ke Vichar'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title of Vichar *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter title of vichar"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <div className="bg-white">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                className=" mb-12"
                placeholder="Enter description (no word limit)"
              />
            </div>
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
              ) : (
                <>{isEditing ? 'Update' : 'Create'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Vicharniye Mahurat Tab ───────────────────────────────────────────────────

const VicharniyeMahuratTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });

  useEffect(() => { fetchItems(); }, [currentPage]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllVicharniyeMahurat({ page: currentPage, limit: 10 });
      if (response.success) {
        setItems(response.data || []);
        setPagination(response.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
      }
    } catch (error) {
      console.error('Error fetching Vicharniye Mahurat:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    !searchTerm ||
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => { setSelectedItem(null); setIsEditing(false); setIsModalOpen(true); };
  const handleEdit = (item) => { setSelectedItem(item); setIsEditing(true); setIsModalOpen(true); };
  const handleDelete = (item) => { setSelectedItem(item); setIsDeleteModalOpen(true); };

  const confirmDelete = async () => {
    try {
      const response = await ApiService.deleteVicharniyeMahurat(selectedItem.id);
      if (response.success) {
        toast.success('Vicharniye Mahurat deleted!');
        await fetchItems();
        setIsDeleteModalOpen(false);
        setSelectedItem(null);
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSave = async () => { await fetchItems(); setIsModalOpen(false); setSelectedItem(null); };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">विचारणीय मुहूर्त</h2>
          <p className="text-gray-600 mt-1">Manage Vicharniye Mahurat entries</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Vicharniye Mahurat
        </button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by title or description..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full max-w-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-scroll overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sloka</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profit & Loss</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 max-w-xs">{item.title}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-sm line-clamp-2">
                    {item.description?.replace(/<[^>]*>?/gm, '')?.replace(/&nbsp;/g, ' ')}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-xs truncate">{item.sloka || '—'}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-xs truncate">{item.profitAndLoss || '—'}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(item)} className="text-indigo-600 hover:text-indigo-800 p-1 rounded transition-colors" title="Edit">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-800 p-1 rounded transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredItems.length === 0 && !loading && (
          <div className="text-center py-12">
            <Star className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Vicharniye Mahurat found</h3>
            <p className="text-gray-600 mb-4">{searchTerm ? 'Try adjusting your search.' : 'Add your first entry.'}</p>
            {!searchTerm && (
              <button onClick={handleAdd} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add First Entry
              </button>
            )}
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={pagination.currentPage === 1}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
          <span className="px-4 py-2 text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, pagination.totalPages))} disabled={pagination.currentPage === pagination.totalPages}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
        </div>
      )}

      <VicharniyeMahuratModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} item={selectedItem} isEditing={isEditing} />
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={confirmDelete}
        title="Delete Vicharniye Mahurat" message={`Are you sure you want to delete "${selectedItem?.title}"? This action cannot be undone.`} />
    </div>
  );
};

// ─── Vedon ke Vichar Tab ──────────────────────────────────────────────────────

const VedonKeVicharTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });

  useEffect(() => { fetchItems(); }, [currentPage]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllVedonKeVichar({ page: currentPage, limit: 10 });
      if (response.success) {
        setItems(response.data || []);
        setPagination(response.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
      }
    } catch (error) {
      console.error('Error fetching Vedon ke Vichar:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    !searchTerm ||
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => { setSelectedItem(null); setIsEditing(false); setIsModalOpen(true); };
  const handleEdit = (item) => { setSelectedItem(item); setIsEditing(true); setIsModalOpen(true); };
  const handleDelete = (item) => { setSelectedItem(item); setIsDeleteModalOpen(true); };

  const confirmDelete = async () => {
    try {
      const response = await ApiService.deleteVedonKeVichar(selectedItem.id);
      if (response.success) {
        toast.success('Vedon ke Vichar deleted!');
        await fetchItems();
        setIsDeleteModalOpen(false);
        setSelectedItem(null);
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSave = async () => { await fetchItems(); setIsModalOpen(false); setSelectedItem(null); };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">वेदों के विचार</h2>
          <p className="text-gray-600 mt-1">Manage Vedon ke Vichar entries</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Vedon ke Vichar
        </button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by title or description..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full max-w-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{item.title}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-lg line-clamp-2">
                    {item.description?.replace(/<[^>]*>?/gm, '')?.replace(/&nbsp;/g, ' ')}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(item)} className="text-indigo-600 hover:text-indigo-800 p-1 rounded transition-colors" title="Edit">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-800 p-1 rounded transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredItems.length === 0 && !loading && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Vedon ke Vichar found</h3>
            <p className="text-gray-600 mb-4">{searchTerm ? 'Try adjusting your search.' : 'Add your first entry.'}</p>
            {!searchTerm && (
              <button onClick={handleAdd} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add First Entry
              </button>
            )}
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={pagination.currentPage === 1}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
          <span className="px-4 py-2 text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, pagination.totalPages))} disabled={pagination.currentPage === pagination.totalPages}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
        </div>
      )}

      <VedonKeVicharModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} item={selectedItem} isEditing={isEditing} />
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={confirmDelete}
        title="Delete Vedon ke Vichar" message={`Are you sure you want to delete "${selectedItem?.title}"? This action cannot be undone.`} />
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const VacharManagement = () => {
  const [activeTab, setActiveTab] = useState('vicharniyeMahurat');

  const tabs = [
    { id: 'vicharniyeMahurat', label: 'विचारणीय मुहूर्त', sublabel: 'Vicharniye Mahurat' },
    { id: 'vedonKeVichar', label: 'वेदों के विचार', sublabel: 'Vedon ke Vichar' },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Vichar Management</h1>
        <p className="text-gray-600 mt-1">Manage Vicharniye Mahurat and Vedon ke Vichar</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="font-bold">{tab.label}</div>
              <div className="text-xs text-gray-400">{tab.sublabel}</div>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'vicharniyeMahurat' && <VicharniyeMahuratTab />}
      {activeTab === 'vedonKeVichar' && <VedonKeVicharTab />}
    </div>
  );
};

export default VacharManagement;
